import { PATH_WIDTH } from 'constants/map';
import { ZoneContext } from 'context/ZoneContext';
import DirectionIcon from 'pages/Map/components/DirectionIcon';
import { Direction } from 'types';
import { memo, useCallback, useContext } from 'react';
import { Path } from '../Path/Path';
import { Indicator } from '../Path/types';
import { createPathFromPoints } from '../Path/utils';
import { MapTourContext } from 'context/MapTourContext';
import { useTheme } from 'styled-components';
import { TourType } from 'pages/Map/components/MapTour';
import { PathGroup } from './PathRenderer.styled';

export enum PathRendererTestIDs {
    SelectedStrokePath = 'selected-stroke-path',
    LoopBackStrokePath = 'loopback-stroke-path',
    Path = 'path'
}

export const PathRenderer = memo(({ path }: { path: Path }) => {
    const { setSelectedPath, selectedPath, expandedStation, setExpandedStation } = useContext(ZoneContext);
    const { tour, isCurrentTourItem } = useContext(MapTourContext);
    const { colors } = useTheme();

    const isPathSelectedInTour = isCurrentTourItem(TourType.INFO_ABOUT_PATHS);

    const isPathSelected = selectedPath
        ? path.getId() === selectedPath.getId()
        : isPathSelectedInTour && tour
        ? path.getId() === tour.path?.getId()
        : false;
    const { path: svgPath, indicators } = createPathFromPoints(path.getPoints(), path.getIndicators());

    const onPathSelection = useCallback(() => {
        const isPathOfExpandedSubzone =
            expandedStation &&
            [path.source.parentStation?.id, path.target.parentStation?.id, path.source.id, path.target.id].includes(expandedStation.id);
        if (expandedStation && !isPathOfExpandedSubzone) {
            setExpandedStation(undefined);
            return;
        }

        setSelectedPath(path);
    }, [path, setSelectedPath, setExpandedStation, expandedStation]);

    if (path.isHidden(expandedStation)) {
        return null;
    }

    const showLoopbackStrokePath = !isPathSelected && path.isLoopBack;
    const strokeWidth = showLoopbackStrokePath ? PATH_WIDTH - 10 : PATH_WIDTH;

    return (
        <PathGroup
            onClick={onPathSelection}
            style={{ strokeWidth, stroke: path.getPathColor() }}
            isPathSelectedInTour={isPathSelectedInTour}
        >
            {isPathSelected && (
                <path
                    className="selected-path"
                    d={svgPath}
                    data-testid={PathRendererTestIDs.SelectedStrokePath}
                    fill="none"
                    stroke={isPathSelectedInTour ? colors.coral[500] : colors.black}
                    strokeDasharray={'6 4'}
                    strokeWidth={PATH_WIDTH + 2.5}
                />
            )}
            {showLoopbackStrokePath && (
                <path
                    data-testid={PathRendererTestIDs.LoopBackStrokePath}
                    d={svgPath}
                    fill="none"
                    stroke={path.getStrokePathColor()}
                    strokeWidth={PATH_WIDTH}
                />
            )}
            <path d={svgPath} data-testid={PathRendererTestIDs.Path} />
            {indicators.map(({ type, x, y }: Indicator) => {
                const key = `${x}-${y}-${type}`;
                if (type == Direction.DOWN) {
                    return <DirectionIcon key={key} type={Direction.DOWN} x={x} y={y} />;
                }
                if (type == Direction.LEFT) {
                    return <DirectionIcon key={key} type={Direction.LEFT} x={x} y={y} />;
                }
                if (type == Direction.RIGHT) {
                    return <DirectionIcon key={key} type={Direction.RIGHT} x={x} y={y} />;
                }
                return <DirectionIcon key={key} type={Direction.UP} x={x} y={y} />;
            })}
        </PathGroup>
    );
});
